import React, { useState, useEffect, useCallback } from 'react';
import ScSpinner from '../ScSpinner';
import { fetchProfiles, createProfile, updateProfile } from '../../studyConfigurationApi';
import { DEFAULT_PROFILES } from '../../studyConfigurationDefaults';

const codeKey = (code) => (code ?? '').trim();

const ProfilesTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editingKey, setEditingKey] = useState(null);
  const [draft, setDraft] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchProfiles();
      setRows(res.data || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load profiles.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEdit = (row) => {
    setEditingKey(row.profileNo);
    setDraft({
      profileNo: row.profileNo,
      profileCode: row.profileCode,
      profileDescription: row.profileDescription ?? '',
      isActive: row.isActive,
    });
    setError(null);
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setDraft(null);
  };

  const handleSave = async () => {
    if (!draft) return;
    setSaving(true);
    setError(null);
    try {
      await updateProfile(draft.profileNo, draft);
      setEditingKey(null);
      setDraft(null);
      setSuccess(null);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const seedDefaultProfiles = async () => {
    setError(null);
    setSuccess(null);
    const existing = new Set(rows.map((r) => codeKey(r.profileCode)));
    const missing = DEFAULT_PROFILES.filter(
      (p) => !existing.has(codeKey(p.profileCode))
    );
    const alreadyCount = DEFAULT_PROFILES.length - missing.length;

    if (missing.length === 0) {
      setSuccess('All default profiles already exist.');
      return;
    }

    setSeeding(true);
    let inserted = 0;
    let failed = 0;
    let lastError = null;

    for (const profile of missing) {
      try {
        await createProfile({
          profileCode: profile.profileCode,
          profileDescription: profile.profileDescription,
          isActive: true,
        });
        inserted += 1;
      } catch (e) {
        failed += 1;
        lastError = e.response?.data?.error || 'Insert failed.';
      }
    }

    setSeeding(false);
    await load();

    if (inserted > 0) {
      const parts = [`Inserted ${inserted} profile${inserted === 1 ? '' : 's'}.`];
      if (alreadyCount > 0) parts.push(`${alreadyCount} already existed.`);
      if (failed > 0) parts.push(`${failed} failed.`);
      setSuccess(parts.join(' '));
      if (failed > 0 && lastError) setError(lastError);
    } else if (lastError) {
      setError(lastError);
    }
  };

  if (loading) return <ScSpinner />;

  return (
    <div className="sc-profiles">
      {error && <div className="sc-alert sc-alert-error">{error}</div>}
      {success && <div className="sc-alert sc-alert-success">{success}</div>}
      <div className="sc-toolbar">
        <button
          type="button"
          className="sc-btn sc-btn-secondary"
          onClick={seedDefaultProfiles}
          disabled={seeding || editingKey != null}
        >
          {seeding ? 'Inserting…' : 'Insert Default Profiles'}
        </button>
      </div>

      <div className="sc-grid-wrap">
        <table className="sc-grid">
          <thead>
            <tr>
              <th>Code</th>
              <th>Description</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const editable = editingKey === row.profileNo;
              return (
                <tr key={row.profileNo} className={editable ? 'sc-row-editing' : ''}>
                  <td>{row.profileCode}</td>
                  <td>
                    {editable && draft ? (
                      <input
                        className="sc-inline-input"
                        value={draft.profileDescription}
                        disabled={saving}
                        onChange={(e) => setDraft({ ...draft, profileDescription: e.target.value })}
                      />
                    ) : (
                      row.profileDescription ?? '—'
                    )}
                  </td>
                  <td className="sc-actions-cell">
                    {editable ? (
                      <>
                        <button
                          type="button"
                          className="sc-btn sc-btn-primary sc-btn-sm"
                          disabled={saving}
                          onClick={handleSave}
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          className="sc-btn sc-btn-secondary sc-btn-sm"
                          disabled={saving}
                          onClick={cancelEdit}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="sc-btn sc-btn-link"
                        disabled={editingKey != null}
                        onClick={() => startEdit(row)}
                      >
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {rows.length === 0 && (
        <p className="sc-empty">No profiles found. Use Insert Default Profiles to add standard roles.</p>
      )}
    </div>
  );
};

export default ProfilesTab;
